import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  FaArrowRight, 
  FaIndustry, 
  FaGlobe, 
  FaWarehouse, 
  FaBuilding,
  FaRocket,
  FaShieldAlt,
  FaSearch,
  FaChartLine,
  FaHandshake,
  FaLeaf,
  FaMapMarkerAlt,
  FaCertificate
} from "react-icons/fa";

const Card = ({ card, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  const gradients = [
    { from: "from-red-700", to: "to-red-600", bg: "bg-red-50", border: "border-red-200" },
    { from: "from-gray-800", to: "to-gray-700", bg: "bg-gray-50", border: "border-gray-200" },
    { from: "from-red-600", to: "to-red-500", bg: "bg-red-50", border: "border-red-200" },
    { from: "from-gray-900", to: "to-gray-800", bg: "bg-gray-50", border: "border-gray-200" },
    { from: "from-red-800", to: "to-red-700", bg: "bg-red-50", border: "border-red-200" },
    { from: "from-gray-700", to: "to-gray-600", bg: "bg-gray-50", border: "border-gray-200" }
  ];

  const { from, to, bg, border } = gradients[index % gradients.length];
  const gradientClass = `bg-gradient-to-r ${from} ${to}`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="group relative"
    >
      <div className={`h-full bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border ${border} ${isHovered ? 'border-red-300' : ''}`}>
        {/* Card Header with Gradient */}
        <div className={`h-48 relative overflow-hidden`}>
          {/* Image with Overlay */}
          <img
            src={card.image}
            alt={card.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className={`absolute inset-0 ${gradientClass} opacity-20`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          
          {/* Index Badge */}
          <div className="absolute top-4 left-4">
            <motion.div 
              className={`w-12 h-12 rounded-xl ${gradientClass} flex items-center justify-center shadow-lg`}
              animate={isHovered ? { rotate: 360 } : { rotate: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-white font-bold text-lg">{index + 1}</span>
            </motion.div>
          </div>

          {/* Continent Badge */}
          <div className="absolute top-4 right-4">
            <div className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full flex items-center gap-2 shadow-lg">
              <FaMapMarkerAlt className="w-3 h-3 text-red-600" />
              <span className="text-xs font-bold text-gray-800">{card.continent}</span>
            </div>
          </div>
        </div>

        {/* Card Content */}
        <div className="p-8">
          {/* Category Tag */}
          <div className="inline-block mb-4">
            <span className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${bg} text-gray-700 rounded-lg border ${border}`}>
              {card.category}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight group-hover:text-red-700 transition-colors duration-300">
            {card.title}
          </h3>

          {/* Description */}
          <div className="text-gray-600 leading-relaxed mb-6 line-clamp-3">
            {card.description}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            {card.stats.map((stat, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.05 }}
                className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="text-xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Footer */}
          <div className="pt-6 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg ${gradientClass} flex items-center justify-center`}>
                {card.icon === 'industry' && <FaIndustry className="w-4 h-4 text-white" />}
                {card.icon === 'globe' && <FaGlobe className="w-4 h-4 text-white" />}
                {card.icon === 'chart' && <FaChartLine className="w-4 h-4 text-white" />}
                {card.icon === 'building' && <FaBuilding className="w-4 h-4 text-white" />}
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Yücel Grubu</div>
                <div className="text-xs text-gray-500">{card.year}</div>
              </div>
            </div>
            <motion.button
              whileHover={{ x: 5 }}
              className={`flex items-center gap-2 font-semibold text-sm ${gradientClass} bg-clip-text text-transparent`}
            >
              Detaylı Bilgi
              <FaArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        </div>

        {/* Hover Effect Line */}
        <motion.div
          className={`absolute bottom-0 left-0 right-0 h-1 ${gradientClass}`}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </motion.article>
  );
};

const Cards2 = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const cards = [
    {
      image: "/images/firstcaro/4.jpg",
      title: "CTP Levha Üretiminde Küresel Lider",
      description: "Fibrosan markası ile CTP levha üretiminde dünya standartlarını belirleyen teknoloji lideri.",
      category: "ÜRETİM TEKNOLOJİSİ",
      year: "1983'ten Beri",
      continent: "6 KITA",
      icon: "industry",
      stats: [
        { value: "40+", label: "Ülke" },
        { value: "99.7%", label: "Kalite" },
        { value: "ISO 9001", label: "Sertifika" }
      ]
    },
    {
      image: "/images/firstcaro/7.jpg",
      title: "Global Depolama Çözümleri",
      description: "6 kıtada gerçekleştirilen kritik depolama projeleri ile endüstriyel çözümler sunuyoruz.",
      category: "PROJE MÜHENDİSLİĞİ",
      year: "1971'den Beri",
      continent: "AVRUPA - ASYA",
      icon: "chart",
      stats: [
        { value: "500+", label: "Proje" },
        { value: "6", label: "Kıta" },
        { value: "ISO 14001", label: "Çevre" }
      ]
    },
    {
      image: "/images/firstcaro/8.jpg",
      title: "Kompozit Tedarik Zinciri Lideri",
      description: "Türkiye ve global pazarlarda kompozit sektörünün ana tedarikçisi olarak hizmet veriyoruz.",
      category: "TEDARİK ZİNCİRİ",
      year: "1983'ten Beri",
      continent: "GLOBAL",
      icon: "globe",
      stats: [
        { value: "1000+", label: "Müşteri" },
        { value: "50+", label: "Marka" },
        { value: "7/24", label: "Destek" }
      ]
    },
    {
      image: "/images/cards/4.jpg",
      title: "Almanya: Avrupa Operasyon Merkezi",
      description: "1999'dan beri Almanya'da Avrupa operasyonlarımızın merkezini yönetiyoruz.",
      category: "AVRUPA OPERASYONLARI",
      year: "1999'dan Beri",
      continent: "AVRUPA",
      icon: "building",
      stats: [
        { value: "25+", label: "Yıl" },
        { value: "200+", label: "Proje" },
        { value: "TÜV", label: "Sertifika" }
      ]
    },
    {
      image: "/images/cards/5.jpg",
      title: "İngiltere: İnovasyon ve Tasarım",
      description: "İngiltere operasyonumuz ile Atlantik ötesi pazarlara inovatif çözümler sunuyoruz.",
      category: "BİRLEŞİK KRALLIK",
      year: "2007'den Beri",
      continent: "AVRUPA",
      icon: "chart",
      stats: [
        { value: "UKCA", label: "Sertifika" },
        { value: "150+", label: "Müşteri" },
        { value: "ISO 45001", label: "Güvenlik" }
      ]
    },
    {
      image: "/images/cards/6.jpg",
      title: "ABD: Global Genişleme ve İnovasyon",
      description: "Chicago merkezli operasyonumuz ile Kuzey Amerika pazarında lider konumdayız.",
      category: "KUZEY AMERİKA",
      year: "2010'dan Beri",
      continent: "AMERİKA",
      icon: "industry",
      stats: [
        { value: "ASTM", label: "Standard" },
        { value: "300+", label: "Proje" },
        { value: "UL", label: "Sertifika" }
      ]
    }
  ];

  const filters = [
    { id: 'all', label: 'Tümü', color: 'bg-red-600' },
    { id: 'production', label: 'Üretim', color: 'bg-gray-700' },
    { id: 'projects', label: 'Projeler', color: 'bg-red-700' },
    { id: 'global', label: 'Global Operasyonlar', color: 'bg-gray-800' },
    { id: 'technology', label: 'Teknoloji', color: 'bg-red-800' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-20 bg-white">
      {/* Section Header */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-3 mb-6">
          <div className="w-16 h-1 bg-gradient-to-r from-red-600 to-red-700 rounded-full" />
          <div className="text-sm font-semibold text-red-700 uppercase tracking-wider">Global Başarılarımız</div>
          <div className="w-16 h-1 bg-gradient-to-r from-red-600 to-red-700 rounded-full" />
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
          6 Kıtada <span className="text-red-700">Liderlik</span>
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Dünya genelinde kompozit teknolojilerinin gelişimine yön veren, 
          uluslararası standartlarda çözümler sunan bir küresel markayız.
        </p>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap justify-center gap-3 mb-16">
        {filters.map((filter) => (
          <motion.button
            key={filter.id}
            onClick={() => setActiveFilter(filter.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
              activeFilter === filter.id
                ? `${filter.color} text-white shadow-xl`
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
            }`}
          >
            <div className={`w-2 h-2 rounded-full ${activeFilter === filter.id ? 'bg-white' : filter.color}`} />
            {filter.label}
          </motion.button>
        ))}
      </div>

      {/* Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {cards.map((card, index) => (
          <Card key={index} card={card} index={index} />
        ))}
      </div>

      {/* Global Presence Section */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-24 bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 border border-gray-300 shadow-2xl"
      >
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center">
                <FaGlobe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900">Küresel Ağımız</h3>
                <p className="text-red-600 font-semibold">6 Kıta • 40+ Ülke</p>
              </div>
            </div>
            <p className="text-gray-600 leading-relaxed mb-8 text-lg">
              Kırmızı-Gri-Beyaz renklerimizle temsil ettiğimiz güç, teknoloji ve güveni, 
              <span className="font-semibold text-gray-900"> 6 kıtada</span> faaliyet gösteren operasyonlarımızla birleştiriyoruz.
            </p>
            <div className="grid grid-cols-2 gap-6">
              {[
                { label: 'Üretim Tesisleri', value: '8', color: 'bg-red-100 text-red-700' },
                { label: 'Global Ofis', value: '28', color: 'bg-gray-100 text-gray-700' },
                { label: 'Dağıtım Merkezi', value: '12', color: 'bg-red-100 text-red-700' },
                { label: 'Ar-Ge Merkezi', value: '4', color: 'bg-gray-100 text-gray-700' }
              ].map((item, idx) => (
                <div key={idx} className={`p-5 rounded-xl ${item.color} border border-gray-200`}>
                  <div className="text-3xl font-bold">{item.value}</div>
                  <div className="text-sm font-medium mt-2">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            {/* Simplified World Map with Red Dots */}
            <div className="relative h-64 bg-gradient-to-br from-gray-100 to-white rounded-2xl p-8 border border-gray-300">
              <div className="absolute inset-0 flex items-center justify-center">
                {/* World Map Dots with Red Accents */}
                {[
                  { top: '30%', left: '20%', size: 'w-6 h-6', color: 'bg-red-600', label: 'TR' },
                  { top: '40%', left: '50%', size: 'w-7 h-7', color: 'bg-red-700', label: 'DE' },
                  { top: '45%', left: '45%', size: 'w-6 h-6', color: 'bg-gray-700', label: 'UK' },
                  { top: '50%', left: '15%', size: 'w-7 h-7', color: 'bg-red-800', label: 'US' },
                  { top: '60%', left: '75%', size: 'w-6 h-6', color: 'bg-gray-800', label: 'CN' },
                  { top: '70%', left: '60%', size: 'w-6 h-6', color: 'bg-red-600', label: 'AU' }
                ].map((dot, idx) => (
                  <motion.div
                    key={idx}
                    className={`absolute ${dot.size} ${dot.color} rounded-full border-2 border-white shadow-xl transform -translate-x-1/2 -translate-y-1/2`}
                    style={{ top: dot.top, left: dot.left }}
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: idx * 0.3 }}
                  >
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs font-bold text-gray-900 bg-white px-2 py-1 rounded-lg shadow-sm">
                      {dot.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
                <FaCertificate className="w-4 h-4 text-red-600" />
                <span className="text-sm font-semibold text-gray-700">Uluslararası Sertifikalı</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Certifications */}
      <div className="mt-20">
        <div className="text-center mb-12">
          <h4 className="text-2xl font-bold text-gray-900 mb-4">
            Kalite ve Güvenin Sembolü
          </h4>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Kırmızı rengimizin temsil ettiği güven ve gri-beyazın ifade ettiği teknolojiyi, uluslararası sertifikalarımızla taçlandırıyoruz
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {[
            { name: 'ISO 9001', desc: 'Kalite', color: 'bg-red-50 border-red-200 text-red-700' },
            { name: 'ISO 14001', desc: 'Çevre', color: 'bg-gray-50 border-gray-200 text-gray-700' },
            { name: 'ISO 45001', desc: 'İş Güvenliği', color: 'bg-red-50 border-red-200 text-red-700' },
            { name: 'TÜV', desc: 'Almanya', color: 'bg-gray-50 border-gray-200 text-gray-700' },
            { name: 'UKCA', desc: 'Birleşik Krallık', color: 'bg-red-50 border-red-200 text-red-700' },
            { name: 'ASTM', desc: 'ABD Standardı', color: 'bg-gray-50 border-gray-200 text-gray-700' }
          ].map((cert, idx) => (
            <motion.div 
              key={idx}
              whileHover={{ y: -5 }}
              className={`border rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-all ${cert.color}`}
            >
              <div className="text-xl font-bold mb-2">{cert.name}</div>
              <div className="text-sm font-medium">{cert.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Cards2;